import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { defineMessages, FormattedMessage } from 'react-intl';
import { makeStyles } from '@material-ui/core';
import useMediaDevices from 'react-use/lib/useMediaDevices';

import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import TextField from '@material-ui/core/TextField';
import Tooltip from '@material-ui/core/Tooltip';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';

import { updateMediaStatus } from '../../../../actions';
import getBestMediaResolution from '../../../../services/getBestMediaResolution';
import mediaStatusShape from '../../../../shapes/mediaStatus';
import {
  getDefaultVideo,
  getFilteredVideo, stopStream,
} from '../../../../utils/devices';
import {
  selectUserMediaStatus,
} from '../../../../selectors';
import bugsnagClient from '../../../../bugsnag';
import { getAspectRatio } from '../../../../utils';

const messages = defineMessages({
  CAM_INPUT_LABEL: {
    id: 'CAM_INPUT_LABEL',
    defaultMessage: 'Camera',
  },
  NO_DEVICES: {
    id: 'NO_DEVICES',
    defaultMessage: 'No devices detected',
  },
  BLOCKED_BY_BROWSER: {
    id: 'BLOCKED_BY_BROWSER',
    defaultMessage: 'Blocked by browser',
  },
  CAMERA_DISABLED_TOOLTIP: {
    id: 'CAMERA_DISABLED_TOOLTIP',
    defaultMessage: 'Your camera is blocked by your browser. Click the blocked media icon in your browser\'s address bar to allow camera use and then refresh',
  },
});

const useStyles = makeStyles(() => ({
  customWidth: {
    maxWidth: 240,
  },
}));

let gettingStream = false;

const CamDevice = ({
  mediaStatus,
  updateMediaStatusAction,
}) => {
  const { devices } = useMediaDevices();
  const classes = useStyles();

  const {
    hasCamDevice,
    hasCamPermissions,
    videoDevice,
    videoDeviceSelect,
    videoStreamSelect,
  } = mediaStatus;

  const [defaultVideo, setDefaultVideo] = useState(null);
  const [videoDevices, setVideoDevices] = useState([]);

  const selectedVideo = videoDeviceSelect || videoDevice;
  const setSelectedVideo = (selectedDevice) => {
    updateMediaStatusAction({
      userId: mediaStatus.userId,
      videoDeviceSelect: selectedDevice,
    });
  };

  // initialize video
  useEffect(() => {
    if (videoDeviceSelect && !gettingStream) {
      gettingStream = true;
      console.log('CamDevice 1');
      getBestMediaResolution(_.get(videoDeviceSelect, 'deviceId'))
        .then((stream) => {
          updateMediaStatusAction({
            userId: mediaStatus.userId,
            videoStreamSelect: stream,
            videoAspectRatioSelect: getAspectRatio(stream),
          });
        }).catch(console.error);
    }

    return () => {
      gettingStream = false;
    };
  }, [videoDeviceSelect]);

  // update videoInput
  useEffect(() => {
    const myLastVideoInput = localStorage.getItem('myLastVideoInput');
    if (videoDeviceSelect && myLastVideoInput !== _.get(videoDeviceSelect, 'deviceId')) {
      localStorage.setItem('myLastVideoInput', _.get(videoDeviceSelect, 'deviceId'));

      if (videoStreamSelect) {
        stopStream(videoStreamSelect);
        updateMediaStatusAction({
          userId: mediaStatus.userId,
          videoStreamSelect: null,
        });
      }

      console.log('CamDevice 2');
      getBestMediaResolution(_.get(videoDeviceSelect, 'deviceId'))
        .then((stream) => {
          updateMediaStatusAction({
            userId: mediaStatus.userId,
            videoStreamSelect: stream,
            videoAspectRatioSelect: getAspectRatio(stream),
          });
        }).catch((err) => {
          console.error(err);
          bugsnagClient.notify(err);
        });
    }
  }, [videoDeviceSelect]);

  // initialize devices
  useEffect(() => {
    if (devices) {
      const filteredDefaultVideo = getDefaultVideo(devices);
      const filteredVideoDevices = getFilteredVideo(devices);
      setDefaultVideo(filteredDefaultVideo);
      setVideoDevices(filteredVideoDevices);
      setSelectedVideo(selectedVideo);
    }
  }, [devices]);

  // check if selected video device is still connected
  useEffect(() => {
    if (selectedVideo) {
      const filter = {};
      if (selectedVideo.groupId) {
        filter.groupId = selectedVideo.groupId;
      } else {
        filter.deviceId = selectedVideo.deviceId;
      }

      const stillExists = !!_.filter(videoDevices, filter).length;

      if (!stillExists) {
        setSelectedVideo(null);
      }
    }
  }, [videoDevices]);

  const handleChange = (setter, filter, localDevices) => (e) => {
    const id = e.target.value;
    const match = _.find(filter(localDevices), { groupId: id });
    if (match) {
      setter(match);
    } else {
      setter(_.find(filter(localDevices), { deviceId: id }));
    }
  };

  const selectedDevice = _.get(selectedVideo, 'groupId')
    || _.get(selectedVideo, 'deviceId')
    || _.get(defaultVideo, 'groupId')
    || _.get(defaultVideo, 'deviceId')
    || '';
  const [openTT, setOpen] = useState(false);
  const handleClick = () => { setOpen((prev) => !prev); };
  const handleClickAway = () => { setOpen(false); };

  return (
    <>
      <InputLabel id="cam">
        <FormattedMessage {...messages.CAM_INPUT_LABEL} />
      </InputLabel>
      {hasCamDevice && hasCamPermissions !== false && (
        <TextField
          select
          id="DeviceSettingsCam"
          labelid="cam"
          variant="filled"
          margin="dense"
          value={selectedDevice}
          onChange={handleChange(setSelectedVideo, getFilteredVideo, videoDevices)}
          disabled={!selectedVideo && !!defaultVideo}
          fullWidth
        >
          {videoDevices.map((device) => (
            <MenuItem
              key={device.groupId || device.deviceId}
              value={device.groupId || device.deviceId}
            >
              {device.label}
            </MenuItem>
          ))}
        </TextField>
      )}
      {(!hasCamDevice || hasCamPermissions === false) && (
        <ClickAwayListener onClickAway={handleClickAway}>
          <Tooltip
            title={<FormattedMessage {...messages.CAMERA_DISABLED_TOOLTIP} />}
            classes={{ tooltip: classes.customWidth }}
            open={openTT}
          >
            <TextField
              select
              onClick={handleClick}
              labelid="cam"
              variant="filled"
              margin="dense"
              value={1}
              fullWidth
              disabled
            >
              {!hasCamDevice && (
              <MenuItem value={1}>
                <FormattedMessage {...messages.NO_DEVICES} />
              </MenuItem>
              )}
              {!hasCamPermissions && (
              <MenuItem value={1}>
                <FormattedMessage {...messages.BLOCKED_BY_BROWSER} />
              </MenuItem>
              )}
            </TextField>
          </Tooltip>
        </ClickAwayListener>
      )}
    </>
  );
};

CamDevice.propTypes = {
  mediaStatus: mediaStatusShape.propTypesShape.isRequired,
  updateMediaStatusAction: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  mediaStatus: selectUserMediaStatus(state),
});

const mapDispatchToProps = (dispatch) => bindActionCreators({
  updateMediaStatusAction: updateMediaStatus,
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(CamDevice);
